/**
 * Also-serving coverage band helpers (wayfinder L9 addendum #9/#10).
 *
 * Coverage-without-building lives in bb_locations metro rows (the projection
 * sources service_locations from `metro`, not the chips column). These
 * helpers are the single validator for anything that wants to CREATE one:
 * a label is only storable when it matches the "City, ST" shape — the
 * Postel boundary that keeps "asdf" out of the coverage band.
 */
import { slugFromLocationName } from './locationDisplay';

/** "City, ST" only — city may contain letters, spaces, periods, apostrophes,
 *  hyphens; state is exactly two letters. No ZIPs, no counties, no free text. */
const COVERAGE_PATTERN = /^([A-Za-z][A-Za-z .'-]*),\s*([A-Za-z]{2})$/;

export const COVERAGE_HINT = 'Use "City, ST" — e.g. Round Rock, TX';

/**
 * Normalize a raw input to a storable "City, ST" label, or null when the
 * input is not that shape. Case-insensitive in, canonical out
 * ("round rock, tx" → "Round Rock, TX").
 */
export function normalizeCoverageLabel(raw: string): string | null {
  const collapsed = raw.trim().replace(/\u00a0/g, ' ').replace(/\s+/g, ' ');
  const match = collapsed.match(COVERAGE_PATTERN);
  if (!match) return null;
  const city = match[1]
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
  return `${city}, ${match[2].toUpperCase()}`;
}

/** Stable location_key for a coverage row ("Round Rock, TX" → "round-rock-tx"). */
export function coverageRowKey(label: string): string {
  return slugFromLocationName(label) || 'coverage';
}

/** Case-insensitive membership test against existing metro labels. */
export function hasCoverageLabel(labels: string[], label: string): boolean {
  const key = label.trim().toLowerCase();
  return labels.some((value) => value.trim().toLowerCase() === key);
}
